import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, X } from "lucide-react";
import { Chateau } from "@/hooks/useChateaux";

interface ChateauFormDialogProps {
  chateau?: Chateau;
  onSave: (chateauData: any) => Promise<void>;
  trigger?: React.ReactNode;
  title?: string;
}

export const ChateauFormDialog = ({ 
  chateau, 
  onSave, 
  trigger,
  title = "Ajouter un Château"
}: ChateauFormDialogProps) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: chateau?.id || "",
    nom: chateau?.nom || "",
    taille: chateau?.taille || "",
    capacite: chateau?.capacite || "",
    prix: chateau?.prix || "",
    description: chateau?.description || "",
    caracteristiques: chateau?.caracteristiques || [],
    age_recommande: chateau?.age_recommande || "3-12 ans",
    is_visible: chateau?.is_visible ?? true,
    display_order: chateau?.display_order || 0
  });
  const [newCaracteristique, setNewCaracteristique] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onSave(formData);
      setOpen(false);
      
      // Reset form if creating new
      if (!chateau) {
        setFormData({
          id: "",
          nom: "",
          taille: "",
          capacite: "",
          prix: "",
          description: "",
          caracteristiques: [],
          age_recommande: "3-12 ans",
          is_visible: true,
          display_order: 0
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCaracteristique = () => {
    if (newCaracteristique.trim()) {
      setFormData(prev => ({
        ...prev,
        caracteristiques: [...prev.caracteristiques, newCaracteristique.trim()]
      }));
      setNewCaracteristique("");
    }
  };

  const removeCaracteristique = (index: number) => {
    setFormData(prev => ({
      ...prev,
      caracteristiques: prev.caracteristiques.filter((_, i) => i !== index)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCaracteristique();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            {title}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="id">ID du château *</Label>
              <Input
                id="id"
                value={formData.id}
                onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
                placeholder="ex: disco-m"
                required
                disabled={!!chateau} // Disable editing ID for existing chateaux
              />
            </div>
            <div>
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Château Disco M"
                required
              />
            </div>
            <div>
              <Label htmlFor="taille">Taille *</Label>
              <Input
                id="taille"
                value={formData.taille}
                onChange={(e) => setFormData(prev => ({ ...prev, taille: e.target.value }))}
                placeholder="3,5m x 3,5m x 4m"
                required
              />
            </div>
            <div>
              <Label htmlFor="capacite">Capacité *</Label>
              <Input
                id="capacite"
                value={formData.capacite}
                onChange={(e) => setFormData(prev => ({ ...prev, capacite: e.target.value }))}
                placeholder="8 enfants"
                required
              />
            </div>
            <div>
              <Label htmlFor="prix">Prix *</Label>
              <Input
                id="prix"
                value={formData.prix}
                onChange={(e) => setFormData(prev => ({ ...prev, prix: e.target.value }))}
                placeholder="30€"
                required
              />
            </div>
            <div>
              <Label htmlFor="age_recommande">Âge recommandé *</Label>
              <Input
                id="age_recommande"
                value={formData.age_recommande}
                onChange={(e) => setFormData(prev => ({ ...prev, age_recommande: e.target.value }))}
                placeholder="3-12 ans"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description du château..."
              required
              rows={3}
            />
          </div>

          <div>
            <Label>Caractéristiques</Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  value={newCaracteristique}
                  onChange={(e) => setNewCaracteristique(e.target.value)}
                  placeholder="Ajouter une caractéristique..."
                  onKeyPress={handleKeyPress}
                />
                <Button type="button" onClick={addCaracteristique}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.caracteristiques.map((carac, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {carac}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 w-4 h-4"
                      onClick={() => removeCaracteristique(index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="is_visible"
                checked={formData.is_visible}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_visible: checked }))}
              />
              <Label htmlFor="is_visible">Visible sur le site</Label>
            </div>
            <div>
              <Label htmlFor="display_order">Ordre d'affichage</Label>
              <Input
                id="display_order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                placeholder="0"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? "Sauvegarde..." : "Sauvegarder"}
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};